import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';
import * as React from 'react';
import { AutoSizer } from 'react-virtualized';
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  IAggregateWithMetric,
  IComputedAggregate,
  ISelectedLabelKey,
  ITest,
} from '../../../../data/types';
import { roundValue } from '../../../../utils/math';
import { legendFormatter } from '../../../shared/graphs/legendFormatter';
import { MetricTooltip } from './MetricTooltip';

const CustomDot = (props) => {
  const e = props.e;

  const [r, setR] = React.useState(0);
  let grow = true;
  React.useEffect((): (() => void) => {
    let localR = r;
    const minimum = 5;
    const maximum = 10;
    const growAndShrinkMinimum = 0.1;
    const growAndShrinkMaximum = 0.5;
    let o = 0;
    const interval = setInterval((): void => {
      const determineGrow = (): number => {
        const mGrowAndShrinkMinimum = 0.05;
        const mGrowAndShrinkMaximum = 0.5;
        const dif = (maximum - localR) / minimum;
        // let largestDif = maximum - minimum;
        const growAndShrinkDif = mGrowAndShrinkMaximum - mGrowAndShrinkMinimum;
        return dif * growAndShrinkDif + mGrowAndShrinkMinimum;
      };
      const determineShrink = (): number => {
        // This will get some number between (max - min) and 0
        const dif = localR - minimum;
        const range = maximum - minimum;
        let difRange = dif / range;
        if (difRange < 0) {
          difRange = 0;
        }
        const growAndShrinkDif = growAndShrinkMaximum - growAndShrinkMinimum;
        const firstStep = difRange * growAndShrinkDif;
        let secondStep = growAndShrinkDif - firstStep;
        if (secondStep < 0) secondStep = 0;
        const finalStep = secondStep + growAndShrinkMinimum;
        return finalStep;
      };
      if (grow) {
        if (localR >= 10) {
          if (o <= 6) {
            o++;
            return;
          }
          o = 0;
          grow = false;
          return;
        }
        localR = localR + determineGrow();
        setR(localR);
      } else {
        if (localR <= 5) {
          grow = true;
          return;
        }
        localR = localR - determineShrink();
        setR(localR);
      }
    }, 1000 / 30);
    return (): void => {
      clearInterval(interval);
    };
  }, []);
  return (
    <circle
      style={{ cursor: 'pointer' }}
      cx={e.cx}
      cy={e.cy}
      fill={e.fill}
      height={e.height}
      width={e.width}
      y={e.y}
      x={e.x}
      r={r}
    />
  );
};

interface IProps {
  test?: ITest;
  selectedMetric: ISelectedLabelKey;
  sessions: IComputedAggregate[];
}

interface IScatterPoint {
  metric: IAggregateWithMetric;
  name: string;
  Date: number;
  y: number;
  sessionId: string;
}

const MetricGraphComponent = (props: IProps): React.ReactElement => {
  const data = React.useMemo((): IScatterPoint[] => {
    return orderBy(props.sessions, (agg: IComputedAggregate): number => {
      return +agg.meta.id;
    }).reduce(
      (acc: IScatterPoint[], session: IComputedAggregate): IScatterPoint[] => {
        const metric = session.data[
          props.selectedMetric.key
        ] as IAggregateWithMetric;
        // if session doesn't have values for the select key return early
        if (!metric) return acc;

        acc.unshift({
          metric,
          name: metric.label,
          Date: Number(session.meta.id),
          y: roundValue(metric.mean, metric.roundingPrecision),
          sessionId: session.meta.id,
        });

        return acc;
      },
      []
    );
  }, [JSON.stringify(props.sessions)]);

  const tickFormatter = (tick: string): string =>
    dayjs.utc(Number(tick)).format('M/D/YY');

  // tslint:disable-next-line:no-any
  const dot = (e: any): React.ReactElement => {
    return (
      <circle
        style={{ cursor: 'pointer' }}
        cx={e.cx}
        cy={e.cy}
        fill={e.fill}
        height={e.height}
        width={e.width}
        y={e.y}
        x={e.x}
        r={4}
      />
    );
  };

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
      }}
    >
      <ResponsiveContainer>
        <AutoSizer>
          {(mProps: { width: number; height: number }): React.ReactElement => {
            return (
              <ScatterChart
                width={mProps.width}
                height={mProps.height}
                margin={{ top: 20, right: 10, bottom: 10, left: 0 }}
              >
                <CartesianGrid stroke='#ccc' strokeWidth={0.25} />
                <XAxis
                  type='number'
                  scale='time'
                  dataKey='Date'
                  domain={['auto', 'auto']}
                  tickFormatter={tickFormatter}
                  interval={'preserveStartEnd'}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'var(--color-text)' }}
                />
                <YAxis
                  type='number'
                  dataKey='y'
                  domain={['auto', 'auto']}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'var(--color-text)' }}
                />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={MetricTooltip}
                  formatter={(value: number): number | string => {
                    if (value > 12875256000) {
                      return dayjs.utc(Number(value)).format('M/D/YY');
                    }
                    return value;
                  }}
                />
                <Scatter
                  name='Session Values'
                  data={data}
                  fill='var(--color-accent)'
                  line
                  isAnimationActive={false}
                  lineType='fitting'
                  shape={dot}
                />
                {props.test && (
                  <Scatter
                    isAnimationActive={false}
                    name='Current Test'
                    data={[
                      {
                        name: props.test.label,
                        Date: Number(props.test.sessionId),
                        metric: props.test[props.selectedMetric.key],
                        y: roundValue(
                          props.test[props.selectedMetric.key]?.value ?? 0,
                          props.test[props.selectedMetric.key]
                            ?.roundingPrecision
                        ),
                      },
                    ]}
                    fill='var(--color-scatter-primary)'
                    shape={(e) => {
                      return <CustomDot e={e} />;
                    }}
                  />
                )}

                <Legend formatter={(value) => legendFormatter(value)} />
              </ScatterChart>
            );
          }}
        </AutoSizer>
      </ResponsiveContainer>
    </div>
  );
};

export const MetricGraph = MetricGraphComponent;
